import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_LIMIT = 20;
const normalizeText = (value = "") => String(value).toUpperCase().trim();
const normalizeComparable = (value = "") => normalizeText(value).replace(/[.-]/g, "");

export const useDebouncedCallback = (callback, delay = 300) => {
  const timeoutRef = useRef(null);
  const debounced = useCallback((...args) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => callback(...args), delay);
  }, [callback, delay]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  return debounced;
};

export const useAutocompleteField = ({
  items = [], searchBy, inputValue = "", onSelect, onClear,
  limit = DEFAULT_LIMIT, delay = 300, dropdownSelector = ".dropdown-class",
  normalizeForCompare = normalizeComparable,
}) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const closeDropdown = useCallback(() => {
    setFilteredItems([]);
    setHighlightedIndex(-1);
  }, []);
  const scrollToElement = useCallback((index) => {
    const dropdown = document.querySelector(dropdownSelector);
    const item = dropdown?.children?.[index];
    if (item) item.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [dropdownSelector]);
  const runSearch = useCallback((searchValue) => {
    const upperValue = normalizeText(searchValue);
    if (!upperValue) {
      closeDropdown();
      return;
    }
    const filtered = safeItems
      .filter((item) => item && searchBy(item))
      .filter((item) => normalizeText(searchBy(item)).includes(upperValue))
      .slice(0, limit);
    setFilteredItems(filtered);
    setHighlightedIndex(filtered.length > 0 ? 0 : -1);
  }, [safeItems, searchBy, limit, closeDropdown]);
  const debouncedSearch = useDebouncedCallback(runSearch, delay);
  const isValidInput = useCallback(() => {
    const normalizedInput = normalizeForCompare(inputValue);
    return safeItems.some((item) => {
      const rawValue = searchBy(item);
      return rawValue && normalizeForCompare(rawValue) === normalizedInput;
    });
  }, [safeItems, searchBy, inputValue, normalizeForCompare]);
  const handleSelect = useCallback((item) => {
    if (!item) {
      onClear?.();
      closeDropdown();
      return;
    }
    onSelect(item);
    closeDropdown();
  }, [onSelect, onClear, closeDropdown]);
  const handleKeyDown = useCallback((event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (filteredItems.length === 0 && !normalizeText(inputValue)) {
        const firstItems = safeItems.slice(0, limit);
        setFilteredItems(firstItems);
        setHighlightedIndex(firstItems.length > 0 ? 0 : -1);
        return;
      }
      setHighlightedIndex((previous) => {
        const next = previous < filteredItems.length - 1 ? previous + 1 : previous;
        scrollToElement(next);
        return next;
      });
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((previous) => {
        const next = previous > 0 ? previous - 1 : previous;
        scrollToElement(next);
        return next;
      });
      return;
    }
    if ((event.key === "Enter" || event.key === "Tab") && highlightedIndex >= 0) {
      if (highlightedIndex < filteredItems.length) {
        event.preventDefault();
        handleSelect(filteredItems[highlightedIndex]);
      }
      return;
    }
    if (event.key === "Escape") {
      if (!isValidInput()) onClear?.();
      closeDropdown();
    }
  }, [filteredItems, highlightedIndex, inputValue, safeItems, limit,
    scrollToElement, handleSelect, isValidInput, onClear, closeDropdown]);
  const handleBlur = useCallback((event) => {
    if (event?.currentTarget && !event.currentTarget.contains(event.relatedTarget)) {
      closeDropdown();
    }
  }, [closeDropdown]);

  return {
    filteredItems, highlightedIndex, debouncedSearch, handleKeyDown,
    handleSelect, handleBlur, closeDropdown, setFilteredItems, setHighlightedIndex,
  };
};
